import React from 'react';
import { Link } from 'react-router-dom';
import { Hexagon, ShoppingCart, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { getCategoryColor } from '../../utils/helpers';
import { iconMap, categories } from './helpers';

export const BrowseTab = ({
  loading, listings, category, setCategory, sortBy, setSortBy,
  watchlist, toggleWatchlist, setBuyModal, setDetailListing, formatCurrency,
}) => {
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex gap-1 bg-[#0A0A0A] border border-[#262626] p-1 rounded">
          {categories.map((c) => (
            <button key={c.value} onClick={() => setCategory(c.value)}
              className={`px-3 py-1.5 text-xs rounded transition-colors ${
                category === c.value ? 'bg-gold text-black' : 'text-muted-foreground hover:text-white'
              }`}>{c.label}</button>
          ))}
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="sort-select"
          className="bg-[#0A0A0A] border border-[#262626] text-sm text-muted-foreground rounded px-3 py-1.5">
          <option value="newest">Newest</option>
          <option value="price_low">Price: Low to High</option>
          <option value="price_high">Price: High to Low</option>
          <option value="value">Total Value</option>
        </select>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-80 bg-white/5" />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <div className="luxury-card p-12 text-center" data-testid="empty-marketplace">
          <Hexagon size={48} className="text-[#627EEA]/30 mx-auto mb-4" />
          <h3 className="text-white font-serif text-xl mb-2">No Digital Twins Available</h3>
          <p className="text-muted-foreground text-sm mb-1 max-w-md mx-auto leading-relaxed">
            This marketplace only shows listings from sellers whose Stripe payouts are active, so every purchase you make settles instantly to a verified seller.
          </p>
          <p className="text-muted-foreground text-xs mb-5 max-w-md mx-auto leading-relaxed">
            Either no sellers are ready yet, or the filters above are excluding everything. Check back soon — or list your own asset.
          </p>
          <div className="flex justify-center gap-2">
            <Link to="/opportunities"><Button className="btn-primary">Tokenise an Asset</Button></Link>
            <Link to="/assets"><Button variant="outline" className="border-gold/30 text-gold hover:bg-gold/10">Browse My Assets</Button></Link>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((item) => {
            const Icon = iconMap[item.category] || Hexagon;
            const availPercent = ((item.fractions_available / item.total_fractions) * 100).toFixed(0);
            const isWatched = watchlist.includes(item.id);
            return (
              <div key={item.id} onClick={() => setDetailListing(item)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setDetailListing(item); }} className="luxury-card overflow-hidden group hover:border-[#627EEA]/30 transition-colors cursor-pointer" data-testid={`listing-card-${item.id}`}>
                <div className="relative h-48 bg-[#0A0A0A]">
                  {item.image_url ? (
                    <img src={item.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url} alt={item.asset_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Icon size={48} className={`${getCategoryColor(item.category)} opacity-30`} /></div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-wider bg-black/70 backdrop-blur-sm rounded ${getCategoryColor(item.category)}`}><Icon size={12} />{item.category}</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); toggleWatchlist(item.id); }} className={`absolute top-3 right-3 w-8 h-8 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors ${isWatched ? 'bg-gold/20 border border-gold/50' : 'bg-black/50 border border-white/10 hover:border-gold/30'}`} data-testid={`watch-${item.id}`}>
                    <Eye size={12} className={isWatched ? 'text-gold' : 'text-white/60'} />
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <h3 className="text-white font-serif text-lg truncate">{item.asset_name}</h3>
                    <p className="text-gold text-xl font-semibold mt-1">{formatCurrency(item.total_value)}</p>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">{item.fractions_available.toLocaleString()} available</span>
                      <span className="text-muted-foreground">{availPercent}%</span>
                    </div>
                    <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#627EEA] to-[#3B5998] rounded-full transition-all" style={{ width: `${availPercent}%` }} />
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-[#1A1A1A]">
                    <div>
                      <p className="text-xs text-muted-foreground">Per Fraction</p>
                      <p className="text-white font-medium">{formatCurrency(item.price_per_fraction)}</p>
                    </div>
                    <Button onClick={(e) => { e.stopPropagation(); setBuyModal(item); }} className="bg-[#627EEA] hover:bg-[#5570D0] text-white text-sm px-4" data-testid={`buy-btn-${item.id}`}>
                      <ShoppingCart size={14} className="mr-1" /> Buy
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
